import { Box, Link, Skeleton, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useGetSelfGuidedSurveyServiceQuery } from "__generated__/types";
import { Button, Panel } from "components/common";
import { Service } from "models";
import { FormattedMessage, useIntl } from "services";
import { isMobileDevice } from "tools";
import { QRCodeImage } from "./QRCodeImage";
import { formatDate } from "@movehq/datetime";

const SurveyTips: React.FC<{ maxSurveyDate: string }> = ({ maxSurveyDate }) => {
  const formattedDate = formatDate(maxSurveyDate, "twoDigitDayAndMonth");
  return (
    <Box>
      <Typography variant="sHeading" gutterBottom>
        <FormattedMessage id="surveyPanel.selfGuided.surveyTipHeading" />
      </Typography>

      <Stack component="ul" spacing={1} sx={{ paddingLeft: 2 }}>
        <li>
          <FormattedMessage id="surveyPanel.selfGuided.surveyTip1" />
        </li>
        <li>
          <FormattedMessage id="surveyPanel.selfGuided.surveyTip2" />
        </li>
        <li>
          <FormattedMessage id="surveyPanel.selfGuided.surveyTip3" />
        </li>
        <li>
          <Typography variant="sHeading" component="span">
            <FormattedMessage
              id="surveyPanel.selfGuided.surveyTip4"
              values={{ date: formattedDate }}
            />
          </Typography>
        </li>
      </Stack>
    </Box>
  );
};

const MobileContent: React.FC<{
  surveyLink: string;
  maxSurveyDate: string;
}> = ({ surveyLink, maxSurveyDate }) => {
  return (
    <>
      <Typography variant="sHeading">
        <FormattedMessage id="surveyPanel.selfGuided.mobileDeviceHeading" />
      </Typography>
      <FormattedMessage id="surveyPanel.selfGuided.mobile.subHeading" />
      <Box mt={1}>
        <SurveyTips maxSurveyDate={maxSurveyDate} />
        <Button size="medium" href={surveyLink} target="_blank">
          <FormattedMessage id="surveyPanel.selfGuided.button" />
        </Button>
      </Box>
    </>
  );
};

const DesktopContent: React.FC<{
  surveyLink: string;
  maxSurveyDate: string;
}> = ({ surveyLink, maxSurveyDate }) => {
  return (
    <>
      <Typography variant="sHeading">
        <FormattedMessage id="surveyPanel.selfGuided.mobileDeviceHeading" />
      </Typography>
      <FormattedMessage id="surveyPanel.selfGuided.desktop.subHeading" />
      <Box
        display={"flex"}
        width="100"
        alignItems="flex-start"
        gap={1}
        mt={2}
        sx={{ flexDirection: { xs: "column", md: "row" } }}
      >
        <Box>
          <QRCodeImage url={surveyLink} />
        </Box>
        <Box>
          <SurveyTips maxSurveyDate={maxSurveyDate} />
        </Box>
      </Box>
      <Box gap={0.5} mt={1} display="flex" justifyContent="start">
        <Typography variant="xsBody">
          <FormattedMessage id="surveyPanel.selfGuided.fallbackButton" />
        </Typography>
        <Link href={surveyLink} target="_blank">
          <OpenInNewIcon fontSize="inherit" />
        </Link>
      </Box>
    </>
  );
};

export const SelfGuidedSurveyPanel: React.FC<{ service: Service }> = ({
  service,
}) => {
  const { formatMessage } = useIntl();
  const isMobile = isMobileDevice();

  const { data, loading, error } = useGetSelfGuidedSurveyServiceQuery({
    variables: {
      id: service.id!,
    },
  });

  if (error) {
    return (
      <Typography color="error" sx={{ margin: 1 }}>
        {formatMessage({ id: "surveyPanel.selfGuided.error" })}
      </Typography>
    );
  }

  const surveyService = data?.service;
  if (
    surveyService?.__typename !== "SurveyService" ||
    !surveyService.selfGuidedSurveyLink
  ) {
    return null;
  }
  const { selfGuidedSurveyLink, maxSurveyDate } = surveyService;

  return (
    <Panel
      icon="calendar"
      title={formatMessage({ id: "surveyPanel.selfGuided.heading" })}
      variant="primary"
      ariaLabel={formatMessage({ id: "surveyPanel.selfGuided.heading" })}
    >
      {loading ? (
        <>
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </>
      ) : isMobile ? (
        <MobileContent
          surveyLink={selfGuidedSurveyLink}
          maxSurveyDate={maxSurveyDate}
        />
      ) : (
        <DesktopContent
          surveyLink={selfGuidedSurveyLink}
          maxSurveyDate={maxSurveyDate}
        />
      )}
    </Panel>
  );
};
